/**
 * Records privacy access before a browser-built Metrics CSV is saved.
 * Excel on CJK Windows locales needs a UTF-8 BOM to detect the file encoding (BUG-1383).
 *
 * 1. Send the export type, parsed reason, and row count to the ledger mutation.
 * 2. Map every output union member to a neverthrow result.
 * 3. Save the CSV with a UTF-8 BOM only after the ledger confirms success. Return server query data to the caller.
 * Continues at the Metrics download call site.
 */

import { gql } from '@apollo/client';
import type { DownloadReason } from '@modernloop/shared/components';
import { err, ok, type Result } from 'neverthrow';
import exportFromJSON from 'export-from-json';

import { DataDownloadType, useRecordDataDownloadMutation } from 'src/generated/mloop-graphql';
import type { DownloadFailure } from 'src/components/Dialog/DownloadReasonDialog/types';

/** Selects the complete result union so the generated mutation types cover every server outcome. */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const RecordDataDownload = gql`
  mutation RecordDataDownload($input: RecordDataDownloadInput!) {
    recordDataDownload(input: $input) {
      __typename
      ... on RecordDataDownloadSuccess {
        recorded
      }
      ... on InvalidDownloadReasonError {
        errorCode
        errorMessage
      }
      ... on InvalidRowCountError {
        errorCode
        errorMessage
      }
      ... on PermissionDeniedError {
        errorCode
        errorMessage
      }
      ... on UnknownError {
        errorCode
        errorMessage
      }
    }
  }
`;

/** Holds the browser export arguments until the privacy ledger accepts the request. */
export type CsvExport = {
  data: unknown[];
  fileName: string;
  exportType: 'csv';
};

/**
 * Saves rows from a server query after that query recorded the same reason.
 * The required reason keeps the caller's download decision explicit.
 * The saved CSV starts with a UTF-8 BOM.
 */
export const exportServerRecordedCsv = (downloadReason: DownloadReason, exportOptions: CsvExport): void => {
  void downloadReason;
  exportFromJSON({
    ...exportOptions,
    // Excel on CJK Windows locales needs this BOM to detect UTF-8 (BUG-1383).
    withBOM: true,
  });
};

/** Gives Metrics sections one action that records access before saving a CSV with a UTF-8 BOM. */
export const useExportCsvWithDownloadReason = () => {
  const [recordDataDownload] = useRecordDataDownloadMutation();

  return async (
    downloadType: DataDownloadType,
    downloadReason: DownloadReason,
    exportOptions: CsvExport
  ): Promise<Result<void, DownloadFailure>> => {
    try {
      const response = await recordDataDownload({
        variables: {
          input: { downloadType, downloadReason, rowCount: exportOptions.data.length },
        },
      });
      const output = response.data?.recordDataDownload;
      if (!output) {
        return err({ type: 'DOWNLOAD_FAILED', cause: new Error('Missing download result') });
      }

      switch (output.__typename) {
        case 'RecordDataDownloadSuccess':
          if (output.recorded) {
            exportFromJSON({
              ...exportOptions,
              // Excel on CJK Windows locales needs this BOM to detect UTF-8 (BUG-1383).
              withBOM: true,
            });
            return ok(undefined);
          }
          return err({ type: 'DOWNLOAD_FAILED', cause: output });
        case 'InvalidDownloadReasonError':
        case 'InvalidRowCountError':
        case 'PermissionDeniedError':
        case 'UnknownError':
          return err({ type: 'DOWNLOAD_FAILED', cause: output });
      }
    } catch (cause) {
      return err({ type: 'DOWNLOAD_FAILED', cause });
    }
  };
};

/** Returns query data or a dialog failure when a server download fails or has no rows. */
export const runServerMetricsDownload = async <T extends object>(
  run: () => Promise<T>,
  hasRows: (result: T) => boolean
): Promise<Result<T, DownloadFailure>> => {
  try {
    const result = await run();
    // Apollo lazy queries resolve with `error` instead of throwing; report it as a failed download, not as no data.
    if ('error' in result && result.error) {
      return err({ type: 'DOWNLOAD_FAILED', cause: result.error });
    }
    return hasRows(result) ? ok(result) : err({ type: 'NO_ROWS' });
  } catch (cause) {
    return err({ type: 'DOWNLOAD_FAILED', cause });
  }
};
